// Deployment trigger: 2026-04-24
import React, { useState } from 'react';
import { Search, Loader2, Trophy, Calendar, Gift, User, Phone, Mail, Hash } from 'lucide-react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { Lead, Campaign } from '../types';

const CampaignFinder: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{ lead: Lead, campaign: Campaign | null }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const term = searchTerm.trim();
      const leadsRef = collection(db, 'leads');
      let q;

      // Determine search type based on input
      if (term.includes('@')) {
        q = query(leadsRef, where('projectId', '==', projectId), where('email', '==', term.toLowerCase()), limit(50));
      } else if (/^\+?[\d\s-]+$/.test(term) && term.length >= 7) {
        q = query(leadsRef, where('projectId', '==', projectId), where('phone', '==', term), limit(50));
      } else {
        q = query(leadsRef, where('projectId', '==', projectId), where('ticketNumber', '==', term.toUpperCase()), limit(50));
      }

      let snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // If not found by ticket number, try searching by campaign code directly
        const codeQuery = query(leadsRef, where('projectId', '==', projectId), where('campaignCode', '==', term.toUpperCase()), limit(50));
        snapshot = await getDocs(codeQuery);

        if (snapshot.empty) {
          // If not found, it might be an older lead without campaignCode, or they searched by campaign name
          // Let's find matching campaigns first
          const campaignsRef = collection(db, 'campaigns');
          const campaignsQuery = query(campaignsRef, where('projectId', '==', projectId));
          const campaignsSnap = await getDocs(campaignsQuery);
          
          const matchingCampaigns = campaignsSnap.docs.filter(doc => {
             const data = doc.data();
             const cId = doc.id;
             const cCode = cId.substring(0, 6).toUpperCase();
             return cId === term || 
                    cCode === term.toUpperCase() || 
                    (data.name && data.name.toLowerCase().includes(term.toLowerCase()));
          });
          
          if (matchingCampaigns.length > 0) {
             const campaignIds = matchingCampaigns.map(doc => doc.id);
             const chunks = [];
             for (let i = 0; i < campaignIds.length; i += 10) {
                chunks.push(campaignIds.slice(i, i + 10));
             }
             
             let allDocs: any[] = [];
             for (const chunk of chunks) {
                const cQuery = query(leadsRef, where('projectId', '==', projectId), where('campaignId', 'in', chunk), limit(50));
                const cSnap = await getDocs(cQuery);
                allDocs = [...allDocs, ...cSnap.docs];
             }
             
             snapshot = { empty: allDocs.length === 0, docs: allDocs } as any;
          }

          if (snapshot.empty) {
            // If still not found, try searching by prize won (exact match) just in case
            const prizeQuery = query(leadsRef, where('projectId', '==', projectId), where('prizeWon', '==', term), limit(50));
            snapshot = await getDocs(prizeQuery);
            
            if (snapshot.empty) {
              setError("No customer found with that information.");
              setLoading(false);
              return;
            }
          }
        }
      }

      const fetchedResults = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const leadData = docSnap.data() as Lead;
        leadData.id = docSnap.id;
        try {
          const campaignDoc = await getDoc(doc(db, 'campaigns', leadData.campaignId));
          let campaign = null;
          if (campaignDoc.exists()) {
            campaign = { id: campaignDoc.id, ...campaignDoc.data() } as Campaign;
          }
          return { lead: leadData, campaign };
        } catch (err) {
          console.error("Campaign fetch error:", err);
          return { lead: leadData, campaign: null };
        }
      }));

      // Sort results by date (newest first)
      fetchedResults.sort((a, b) => new Date(b.lead.createdAt).getTime() - new Date(a.lead.createdAt).getTime());
      
      setResults(fetchedResults);

    } catch (err) {
      console.error("Search error:", err);
      setError("An error occurred while searching. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white rounded-[40px] p-8 sm:p-12 border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-indigo-50 rounded-2xl text-indigo-600">
            <Search className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight italic">Campaign Finder</h2>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Verify Customer Rewards</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter Campaign Code, Ticket Code, Email, or Phone..." 
              className="w-full pl-14 pr-6 py-5 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-base font-bold shadow-inner"
            />
          </div>
          <button 
            type="submit"
            disabled={loading || !searchTerm.trim()}
            className="bg-indigo-600 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center min-w-[160px] shadow-xl shadow-indigo-600/20"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
          </button>
        </form>

        {error && (
          <div className="p-6 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold text-center animate-in zoom-in-95">
            {error}
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-8">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-widest">
              Found {results.length} result{results.length !== 1 ? 's' : ''}
            </div>
            
            {results.map((result, index) => (
              <div key={result.lead.id || index} className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-8 duration-500">
                {/* Customer Details */}
                <div className="bg-slate-50 rounded-[32px] p-8 border border-slate-100">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                    <User className="w-4 h-4" /> Customer Information
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Full Name</label>
                      <div className="text-lg font-black text-slate-800">{result.lead.name || 'Anonymous'}</div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                        <div className="text-sm font-bold text-slate-700 truncate">{result.lead.email}</div>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</label>
                        <div className="text-sm font-bold text-slate-700">{result.lead.phone || 'N/A'}</div>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-slate-200">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 flex items-center gap-1"><Hash className="w-3 h-3" /> Ticket / Code</label>
                      <div className="inline-block px-4 py-2 bg-white border border-slate-200 rounded-xl text-lg font-mono font-bold text-indigo-600 tracking-widest shadow-sm">
                        {result.lead.ticketNumber || result.lead.campaignCode || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reward & Campaign Details */}
                <div className="bg-indigo-50 rounded-[32px] p-8 border border-indigo-100">
                  <h3 className="text-xs font-black text-indigo-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> Reward Details
                  </h3>
                  
                  <div className="space-y-8">
                    <div className="bg-white rounded-2xl p-6 border border-indigo-100 shadow-sm">
                      <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-2 flex items-center gap-1"><Gift className="w-3 h-3" /> Prize Won</label>
                      <div className="text-2xl font-black text-indigo-700 uppercase italic tracking-tight">
                        {result.lead.prizeWon || 'No Prize'}
                      </div>
                    </div>

                    {result.campaign ? (
                      <div className="space-y-4">
                        <div>
                          <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">Campaign Name</label>
                          <div className="text-base font-bold text-slate-800">{result.campaign.name}</div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">Game Type</label>
                            <div className="text-sm font-bold text-slate-600 capitalize">{result.campaign.type.replace('_', ' ')}</div>
                          </div>
                          <div>
                            <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date Played</label>
                            <div className="text-sm font-bold text-slate-600">
                              {new Date(result.lead.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm font-bold text-slate-500 italic">
                        Campaign details not available.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignFinder;
